import React, { useEffect } from "react";
import AuthLayout from "../../../components/layouts/auth";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, Minus, TriangleAlert, User } from "lucide-react";
import ToggleSwitch from "../../../components/form/toggle-switch";
import LineLoader from "../../../components/loader/line-loader";
import {
  convert_to_naira,
  numberWithCommas,
  strReplace,
} from "../../../lib/helper-support";
import DefaultModal from "../../../components/modal/default-modal";
import moment from "moment";
import {
  SuccessFlash,
} from "../../../components/helper/flash-notification";
import Naira from "../../../components/naira";
import EditUserAccount from "./partials/edit-user";
import DebitUser from "./partials/debit-user";
import CreditUser from "./partials/credit-user";
import UserTransactionLimit from "./partials/transaction-limit";
import Button from "../../../components/form/button";
import { useApi } from "../../../providers/ApiProvider";
import { useSingleUser } from "../../../composables/users/useSingleUser";

export const getUserRiskBadge = (riskLevel: number) => {

    let risk = 'Red Alert';

    if (riskLevel < 3){
        risk = 'Normal'
    }

    if (riskLevel == 3){
        risk = 'Suspected Activity'
    }

    if (riskLevel == 4){
        risk = 'Suspecious Activity'
    }

    return <span className={`font-bold !text-[10px] border px-2 rounded-full py-1 ${riskLevel >= 3 ? 'text-red-500 bg-red-500/10' : ''}`}>{risk}</span>
  }

const SingleUserAccount: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();

  const api = useApi();

  const { data: user, isLoading } = useSingleUser({
    enabled: !!userId,
    phonenumber: userId as string,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onAccountActivate = async () => {
    const request = await api.POST({
      endpoint: `users/${user.phone_number}/accountActivation`,
      refreshKeys: ["users", user.phone_number],
    });

    if (!request) return;

    SuccessFlash(
      `Account ${!user.is_activated ? "activated" : "deactivated"} successfully`,
    );
  };

  const onAccountBan = async () => {
    const request = await api.POST({
      endpoint: `users/${user.phone_number}/accountBan`,
      refreshKeys: ["users", user.phone_number],
    });

    if (!request) return;

    SuccessFlash(
      `Account ${!user.is_banned ? "banned" : "unbanned"} successfully`,
    );
  };

  const regenerateVA = async () => {
    const request = await api.POST({
      endpoint: `users/${user.phone_number}/regenerateVA`,
      refreshKeys: ["users", user.phone_number],
    });

    if (!request) return;

    SuccessFlash("Virtual account regenerated successfully");
  };

  const switchVerificationStatus = async (status: string) => {
    const request = await api.POST({
      endpoint: `users/${user.phone_number}/switch-verification-stage`,
      payload: {
        status,
      },
    });

    if (!request) return;

    SuccessFlash("Verification status switched successfully");
  };

  

  return (
    <AuthLayout>
      {isLoading && <LineLoader />}
      {!isLoading && user?.phone_number && (
        <div>
          <div>
            <h2 className="text-[#8D8D8D] font-[600] text-[14px]">
              User Accounts / details
            </h2>
            <div className="flex items-center pl-0 mt-4 mb-5 space-x-2">
              <span>
                <Link to={"/user-accounts"}>
                  <ChevronLeft size={20} className="-ml-2" />
                </Link>
              </span>
              <h1 className="font-[600] text-tertiary text-[18px]">
                Cashir user accounts: {user.username}
              </h1>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-5 md:flex md:space-x-2">
              <EditUserAccount user={user} />
              <CreditUser user={user} />
              <DebitUser user={user} />
              <UserTransactionLimit
                user={user}
              />
              <Link to={`/analytics?search=${user.username}`}>
                <Button text="View User Metrics" design="primary-outline" />
              </Link>
            </div>
            <div className="lg:gap-5 lg:flex">
              <div className="lg:w-3/5">
                <div className="grid gap-x-8 md:grid-cols-2 gap-y-4 md:gap-y-0">
                  <div className="grid gap-y-4">
                    <div className="flex space-x-5 h-[6em] items-center">
                      <div>
                        <div className="flex items-center justify-center w-[5em] h-[5em] bg-gray-100 rounded-full">
                          {user.avatar ? <img src={user.avatar} alt="avatar" /> : <User/>}
                        </div>
                      </div>
                      <div className="w-full">
                        <label className="font-[400] text-[13px]">
                          Ctag
                          <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3] pr-[3em] text-left">
                            {user.username}
                          </div>
                        </label>
                        <p className="font-[400] text-[12px] mt-1">
                          <span className="text-primary">
                            {user.deleted_at ? "Created" : "Active"} since:
                          </span>
                          &nbsp;
                          {moment(user.created_at).format(
                            "MMM D, YYYY | h:mm a",
                          )}
                        </p>
                        {user.deleted_at && (
                          <p className="font-[400] text-[12px] mt-2">
                            <span className="text-red-500">Deleted since:</span>
                            &nbsp;
                            {moment(user.deleted_at).format(
                              "MMM D, YYYY | h:mm a",
                            )}
                          </p>
                        )}
                        <p className="font-[400] text-[12px] mt-2">
                          <span>Risk level:</span>
                          &nbsp;
                          {getUserRiskBadge(user.risk_flag)}
                        </p>
                      </div>
                    </div>
                    <div className="h-fit">
                      <label className="font-[400] text-[13px]">
                        Name
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          {user.first_name} {user.last_name}
                        </div>
                        <div className="flex justify-between mt-1">
                          <div className="flex bg-[#F8FAFB] justify-center w-fit py-1 px-2 rounded-full text-xs">
                            <span className="lowercase text-xs">
                              {strReplace(
                                user.verification_stage || "verify_identity",
                                "_",
                                " ",
                              )}
                            </span>
                          </div>
                        </div>
                      </label>
                    </div>
                    <div className="h-fit">
                      <label className="font-[400] text-[13px]">
                        Email
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          {user.email}
                        </div>
                      </label>
                    </div>
                    <div className="h-fit">
                      <label className="font-[400] text-[13px]">
                        Phone number
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          {user.phone_number}
                        </div>
                      </label>
                    </div>
                    <div className="h-fit">
                      <label className="font-[400] text-[13px]">
                        <div className="flex justify-between">
                          <span>Virtual account</span>
                          <span className="font-[400] text-[12px] mt-1">
                            <span className="text-primary">
                              {user.virtual_account?.bank_name ?? ""}
                            </span>
                          </span>
                        </div>
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          {user.virtual_account?.account_number ?? (
                            <Minus size={13} />
                          )}
                        </div>
                      </label>
                    </div>
                    <div className="h-fit">
                      <label className="font-[400] text-[13px]">
                        Gender
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          {user.gender}
                        </div>
                      </label>
                    </div>
                    <div className="h-fit">
                      <label className="font-[400] text-[13px]">
                        Relationship status
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          <Minus size={13} />
                        </div>
                      </label>
                    </div>
                    <div className="flex space-x-1 h-fit">
                      <label className="font-[400] text-[13px] w-3/5">
                        Referral
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          cref_{user.username}
                        </div>
                      </label>
                      <label className="font-[400] text-[13px] w-2/5">
                        Referral counter
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          {user.referral_count}
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="grid gap-y-4">
                    <div className="h-[6em]">
                      <label className="font-[400] text-[13px]">
                        Address of residence
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3] py-[2em]">
                          {user.address_information?.address ?? (
                            <Minus size={13} />
                          )}
                        </div>
                      </label>
                    </div>
                    <div className="h-fit">
                      <label className="font-[400] text-[13px]">
                        LGA of residence
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          {user.address_information?.lga_of_residence ?? (
                            <Minus size={13} />
                          )}
                        </div>
                      </label>
                    </div>
                    <div className="h-fit">
                      <label className="font-[400] text-[13px]">
                        State of origin
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          {user.address_information?.state_of_origin ?? (
                            <Minus size={13} />
                          )}
                        </div>
                      </label>
                    </div>
                    <div className="h-fit">
                      <label className="font-[400] text-[13px]">
                        LGA of origin
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          {user.address_information?.lga_of_origin ?? (
                            <Minus size={13} />
                          )}
                        </div>
                      </label>
                    </div>
                    <div className="h-fit">
                      <label className="font-[400] text-[13px]">
                        Bank verification number (BVN)
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          {user.bvn ?? <Minus size={13} />}
                        </div>
                      </label>
                    </div>
                    <div className="h-fit">
                      <label className="font-[400] text-[13px]">
                        National identification number (NIN)
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          {user.nin_number ?? <Minus size={13} />}
                        </div>
                      </label>
                    </div>
                    <div className="h-fit">
                      <label className="font-[400] text-[13px]">
                        Date of birth
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          {user.date_of_birth ?? <Minus size={13} />}
                        </div>
                      </label>
                    </div>
                    <div className="hidden opacity-0 md:block">
                      <label className="font-[400] text-[13px]">
                        ___
                        <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3]">
                          ____
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-10 lg:pl-5 lg:w-1/3 md:w-1/2 lg:mt-0">
                <div className="font-[400] text-[12px] grid grid-cols-3 gap-4">
                  <div className="w-20">
                    <h5 className="mb-1">Phone number verified</h5>
                    <ToggleSwitch
                      initialState={user.has_verified_phone_number}
                      disableToggle={true}
                    />
                  </div>
                  <div className="w-14">
                    <h5 className="mb-1">Email verified</h5>
                    <ToggleSwitch
                      initialState={user.has_verified_email}
                      disableToggle={true}
                    />
                  </div>
                  <div className="w-20">
                    <h5 className="mb-1">Identity verified</h5>
                    <ToggleSwitch
                      initialState={
                        user.verification_stage == "Identify_Confirmed"
                      }
                      disableToggle={true}
                    />
                  </div>
                </div>
                <div className="mt-8">
                  <div className="h-fit">
                    <label className="font-[400] text-[13px]">
                      Cash balance
                      <div className="text-tertiary font-[600] text-[16px] input w-full border border-[#DCDCDC] bg-[#F3F3F3] transition duration-300 ease-in">
                        <h3 className="flex items-end">
                          <Naira />{" "}
                          {numberWithCommas(
                            convert_to_naira(user.wallet_balance),
                          )}
                          .
                          <span className="text-[11px]">
                            {numberWithCommas(
                              convert_to_naira(user.wallet_balance),
                              true,
                              true,
                            )}
                          </span>
                        </h3>
                      </div>
                    </label>
                  </div>
                </div>
                <div className="mt-3">
                  <div className="flex space-x-2 h-fit">
                    <label className="font-[400] text-[13px]">
                      Account status:{" "}
                      <span
                        className={`font-[700] capitalize ${user.account_status == "deactivated" ? "text-red-500" : ""}`}
                      >
                        {user.deleted_at ? "Deleted" : user.account_status}
                      </span>
                      <DefaultModal
                        icon={<TriangleAlert size={20} />}
                        modalButtonTriggerText={
                          <Button
                            text={user.is_activated ? "Deactivate" : "Activate"}
                          />
                        }
                        description={`Are you sure you'd like to ${user.is_activated ? "deactivate" : "activate"} this user?`}
                        onConfirmationAction={onAccountActivate}
                      />
                    </label>
                    <label className="font-[400] text-[13px]">
                      BVN status:{" "}
                      <span
                        className={`font-[700] capitalize ${user.is_banned ? "text-red-500" : ""}`}
                      >
                        {user.is_banned ? "Banned" : "Not banned"}
                      </span>
                      <DefaultModal
                        icon={<TriangleAlert size={20} />}
                        modalButtonTriggerText={
                          <Button text={user.is_banned ? "Unban" : "Ban"} />
                        }
                        description={`Are you sure you'd like to ${user.is_banned ? "unban" : "ban"} this user?`}
                        onConfirmationAction={onAccountBan}
                      />
                    </label>
                  </div>
                  <div className="flex mt-5 space-x-2 h-fit">
                    <label className="font-[400] text-[13px]">
                      Has Virtual Account Number:{" "}
                      <span className="font-[700] capitalize">
                        {user.virtual_account ? "Yes" : "No"}
                      </span>
                      {!user.virtual_account && user.bvn && (
                        <DefaultModal
                          icon={<TriangleAlert size={20} />}
                          modalButtonTriggerText={<Button text="Create VA" />}
                          description={`Are you sure you'd like to create virtual account number?`}
                          onConfirmationAction={regenerateVA}
                        />
                      )}
                      {user.virtual_account &&
                        user.bvn &&
                        user.account_status === "dormant" && (
                          <DefaultModal
                            icon={<TriangleAlert size={20} />}
                            modalButtonTriggerText={
                              <Button text="Regenerate VA" />
                            }
                            description={`Are you sure you'd like to regenerate virtual account number?`}
                            onConfirmationAction={regenerateVA}
                          />
                        )}
                    </label>
                  </div>
                  {(user.verification_stage ===
                    "awaiting_verification_webhook" || user.verification_stage === "Identify_Unconfirmed") && (
                    <div className="flex-col mt-5 gap-2 h-fit">
                      <label className="label">
                        Change Verification Status
                      </label>
                      <DefaultModal
                        icon={<TriangleAlert size={20} />}
                        modalButtonTriggerText={
                          <Button text="Switch to Verify Face Match" />
                        }
                        description={`Are you sure you'd like to switch verification status?`}
                        onConfirmationAction={() =>
                          switchVerificationStatus("verify_face_match")
                        }
                      />
                    </div>
                  )}
                  <div className="mt-5">
                        <h4 className="label">Government Image</h4>
                        {user.govt_image ? <img width={150} src={user.govt_image.url}/> : 'No image present.'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {!isLoading && !user && (
        <div>
          <h1 className="text-[2em] font-[600] text-primary">
            User Account Not Found
          </h1>
          <Link
            to={"/user-accounts"}
            className="flex items-center mt-5 space-x-2 font-[400] text-gray-500 hover:underline"
          >
            <ChevronLeft size={13} />
            <span>back to users</span>
          </Link>
        </div>
      )}
    </AuthLayout>
  );
};

export default SingleUserAccount;
